/**
 * Success Page
 * Shown after successful payment
 * Displays transaction details from the subscription
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCheckoutSession } from '../hooks';
import { checkoutApi } from '../lib/api';
import { safeRedirect } from '../lib/validation';
import type { Subscription } from '../types/api';
import LoadingSpinner from '../components/LoadingSpinner';

export default function SuccessPage() {
  const { t, i18n } = useTranslation();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);

  // Extract sessionId from URL path
  const pathParts = window.location.pathname.split('/');
  const sessionId = pathParts[pathParts.length - 1] || '';

  // Extract subscriptionId and organizationId from URL params
  const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
  const subscriptionIdParam = params.get('subscriptionId');
  const organizationId = params.get('organization_id') || undefined;

  // Fetch session
  const { session } = useCheckoutSession(sessionId, organizationId);

  useEffect(() => {
    // Set language from session
    if (session?.language && session.language !== i18n.language) {
      i18n.changeLanguage(session.language);
    }
  }, [session, i18n]);

  // Load subscription details
  useEffect(() => {
    const loadSubscription = async () => {
      try {
        if (subscriptionIdParam) {
          const sub = await checkoutApi.getSubscription(subscriptionIdParam);
          setSubscription(sub);
        }
      } catch (err) {
        console.error('Failed to load subscription:', err);
      } finally {
        setSubscriptionLoading(false);
      }
    };

    loadSubscription();
  }, [subscriptionIdParam]);

  // Handle redirect to return URL with validation
  const handleReturnToSaaS = () => {
    const params = new URLSearchParams(window.location.search);
    let returnUrl = params.get('returnUrl');

    if (!returnUrl && session?.return_url) {
      returnUrl = session.return_url;
    }

    // Use safe redirect that validates URL against whitelist
    safeRedirect(returnUrl, () => {
      window.close();
    });
  };

  // Format currency value
  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: currency || 'CLP',
    }).format(amount);
  };

  // Format date
  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('es-CL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="w-full max-w-2xl mx-auto flex flex-col items-center justify-center min-h-[calc(100vh-64px)] px-4 py-8">
      <div className="card w-full">
        {/* Success Icon & Title */}
        <div className="text-center mb-8">
          <div className="status-icon-container status-icon-success mb-6">
            <svg
              className="h-8 w-8"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            {t('success.title')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400">{t('success.message')}</p>
        </div>

        {/* Subscription Details */}
        {subscriptionLoading ? (
          <div className="py-8">
            <LoadingSpinner message={t('success.loadingDetails') || 'Cargando detalles...'} />
          </div>
        ) : subscription ? (
          <div className="space-y-6 mb-8">
            {/* Plan Information */}
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6 border border-blue-200 dark:border-blue-800">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                {t('success.planInfo')}
              </h2>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-600 dark:text-gray-400">{t('success.plan')}</p>
                  <p className="font-semibold text-gray-900 dark:text-white">
                    {subscription.plan_snapshot.name}
                  </p>
                </div>
                <div>
                  <p className="text-gray-600 dark:text-gray-400">{t('success.price')}</p>
                  <p className="font-semibold text-gray-900 dark:text-white">
                    {formatCurrency(subscription.plan_snapshot.price, subscription.plan_snapshot.currency)} /{' '}
                    {subscription.plan_snapshot.interval}
                  </p>
                </div>
                <div>
                  <p className="text-gray-600 dark:text-gray-400">{t('success.email')}</p>
                  <p className="font-semibold text-gray-900 dark:text-white break-all">
                    {subscription.customer_email}
                  </p>
                </div>
                <div>
                  <p className="text-gray-600 dark:text-gray-400">{t('success.status')}</p>
                  <p className="font-semibold text-green-600 dark:text-green-400">
                    {t(`subscription.status.${subscription.status.toLowerCase()}`)}
                  </p>
                </div>
              </div>
            </div>

            {/* Billing Period */}
            <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-6 border border-green-200 dark:border-green-800">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                {t('success.billingInfo')}
              </h2>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-600 dark:text-gray-400">{t('success.periodStart')}</p>
                  <p className="font-semibold text-gray-900 dark:text-white">
                    {formatDate(subscription.current_period_start)}
                  </p>
                </div>
                <div>
                  <p className="text-gray-600 dark:text-gray-400">{t('success.periodEnd')}</p>
                  <p className="font-semibold text-gray-900 dark:text-white">
                    {formatDate(subscription.current_period_end)}
                  </p>
                </div>
                <div>
                  <p className="text-gray-600 dark:text-gray-400">{t('success.nextBilling')}</p>
                  <p className="font-semibold text-gray-900 dark:text-white">
                    {formatDate(subscription.next_billing_date)}
                  </p>
                </div>
                <div>
                  <p className="text-gray-600 dark:text-gray-400">{t('success.subscriptionId')}</p>
                  <p className="font-mono text-xs text-gray-900 dark:text-white break-all">
                    {subscription.subscription_id.substring(0, 12)}...
                  </p>
                </div>
              </div>
            </div>

            {/* Transaction Reference */}
            <div className="bg-gray-50 dark:bg-gray-900/20 rounded-lg p-4 border border-gray-200 dark:border-gray-800">
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">{t('success.flowTransactionId')}</p>
              <p className="font-mono text-sm text-gray-900 dark:text-white break-all">
                {subscription.flow_subscription_id}
              </p>
            </div>
          </div>
        ) : null}

        {/* Action Buttons */}
        <div className="space-y-3">
          {session?.return_url ? (
            <button
              onClick={handleReturnToSaaS}
              className="btn-primary w-full py-3"
            >
              {t('success.returnToApp') || 'Volver a la aplicación'}
            </button>
          ) : (
            <button
              onClick={() => window.close()}
              className="btn-primary w-full py-3"
            >
              {t('success.closeWindow') || 'Cerrar ventana'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
